import Field from '../Field';
import ModeledAudienceDataTable from '../ModeledAudienceDataTable/index.jsx';
import React, { useMemo, useState } from 'react';
import TextFieldSearch from '../TextFieldSearch/TextFieldSearch';
import {
  AUDIENCE_SELECT_EMAIL_AUTO_COMPLETE,
  OPERATOR_TYPE,
} from '../../constants';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Typography,
} from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import { Formik } from 'formik';
import { SELECT_GROUP } from '../../content/index';
import { makeStyles } from '@material-ui/core/styles';
import * as Styled from './index.styles';

const useStyles = makeStyles(theme => ({
  containerBorder: {
    margin: '2rem',
    borderRadius: 8,
    marginBottom: '6rem',
  },
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '0rem',
    marginBottom: '1rem',
    '& > div': {
      boxShadow: 'none',
    },
    '& > div > button': {
      fontFamily: 'Calibre-Medium, sans-serif',
      fontSize: '17px',
      lineHeight: '24px',
      borderRadius: '8px',
      boxShadow: 'none',
      height: '42px',
      padding: 'calc(9px) 24px calc(10px)',
    },
    '& > div > .MuiButton-containedPrimary': {
      backgroundColor: 'rgb(65, 55, 98)',
    },
    '& > div > .MuiButton-containedInherit': {
      backgroundColor: 'white',
      border: '1px solid rgb(65, 55, 98)',
      color: 'rgb(65, 55, 98)',
    },
    '& > div >button:nth-child(1)': {
      borderTopRightRadius: '0px',
      borderBottomRightRadius: '0px',
      borderRightColor: 'transparent',
    },
    '& > div >button:nth-child(2)': {
      borderTopLeftRadius: '0px',
      borderBottomLeftRadius: '0px',
      marginLeft: '-1px',
    },
  },
  operatorText: {
    minWidth: '15px',
    paddingTop: '6px',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  divider: {
    width: '100%',
    color: '#ccc',
    opacity: '0.2',
    marginTop: '0px',
    marginBottom: '0px',
  },
  generateHeaderText: {
    fontSize: '22px',
    fontFamily: 'Calibre-Semibold, sans-serif',
    fontWeight: '600',
    lineHeight: '32px',
  },
}));

const CombinedAudienceBuilder = ({
  selectedAudiences,
  setSelectedAudiences,
  operatorType,
  setOperatorType,
  modeledAudiences,
  isAudienceLoading,
}) => {
  const classes = useStyles();
  const [oprations, setOprations] = useState({
    page: 0,
    rowsPerPage: 25,
    sortBy: 'updatedAt',
    sortOrder: 'DESC',
    search: '',
    email: 'all',
    groupId: 'all',
  });
  const [page, setPage] = React.useState(0);
  const { groupId } = oprations;

  const groupsOptions = useMemo(() => {
    if (modeledAudiences && modeledAudiences.length > 0) {
      const uniqueGroupNames = [
        ...new Set(modeledAudiences?.map(a => a.group_name)),
      ];
      return [
        { label: 'All Groups', id: 'all' },
        ...uniqueGroupNames.map(name => ({ label: name, id: name })),
      ];
    }
    return [];
  }, [modeledAudiences]);

  const emails = useMemo(() => {
    if (modeledAudiences && modeledAudiences.length > 0) {
      const uniqueGroupNames = [
        ...new Set(modeledAudiences?.map(a => a.created_by_email)),
      ];
      return [
        { label: 'All Emails', id: 'all' },
        ...uniqueGroupNames.map(created_by_email => ({
          label: created_by_email,
          id: created_by_email,
        })),
      ];
    }
    return [];
  }, [modeledAudiences]);

  const toggleAudience = audience => {
    setSelectedAudiences(prev =>
      prev.some(a => a.id === audience.id)
        ? prev.filter(i => i.id !== audience.id)
        : [
            ...prev,
            {
              id: audience.id,
              type: audience.audienceType,
              name: audience.name,
              lalModelPrecision: 1,
            },
          ]
    );
  };

  const removeChip = index => {
    setSelectedAudiences(prev => prev.filter(i => i.id !== index));
  };

  return (
    <div className={classes.containerBorder}>
      <Card variant='outlined'>
        <CardContent>
          <div className={classes.generateHeaderText}>Build your Audience</div>
          <div className={classes.buttonGroup}>
            <ButtonGroup variant='contained'>
              <Button
                color={
                  operatorType === OPERATOR_TYPE.OR ? 'primary' : 'inherit'
                }
                onClick={() => setOperatorType('OR')}
              >
                OR
              </Button>
              <Button
                color={operatorType === 'AND' ? 'primary' : 'inherit'}
                onClick={() => setOperatorType('AND')}
              >
                AND
              </Button>
            </ButtonGroup>
          </div>
          {selectedAudiences.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {selectedAudiences.map((item, index) => {
                const audience =
                  modeledAudiences && modeledAudiences.length > 0
                    ? modeledAudiences.find(a => `${a.id}` === `${item.id}`)
                    : null;
                return (
                  <React.Fragment key={item.id}>
                    {audience && (
                      <Chip
                        label={audience.name}
                        onDelete={() => removeChip(audience.id)}
                        sx={{ maxWidth: 300 }}
                      />
                    )}
                    {index !== selectedAudiences.length - 1 && (
                      <div className={classes.operatorText}>{operatorType}</div>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          )}
          <Typography
            variant='subtitle1'
            gutterBottom
            style={{ marginTop: '2rem' }}
          >
            Search & select an audience:
          </Typography>
          <hr className={classes.divider} />

          <Box height='100%'>
            <Styled.SearchArea>
              <>
                <Formik>
                  {() => (
                    <Styled.Form onSubmit={e => e.preventDefault()}>
                      <Field
                        name='searchAudiences'
                        placeholder='Search Audiences'
                        value={oprations.search}
                        style={{ height: 44, width: 415 }}
                        onChange={e => {
                          setPage(0);
                          setOprations({
                            ...oprations,
                            search: e.target.value,
                            page: 0,
                          });
                        }}
                      />
                    </Styled.Form>
                  )}
                </Formik>
              </>

              <Styled.SelectAutoCompleteGroup
                disablePortal
                id='select-audience-group'
                options={groupsOptions}
                disabled={isAudienceLoading}
                popupIcon={<CaretDown />}
                value={
                  groupsOptions.find(a => `${a?.id}` === `${groupId}`)?.label
                }
                renderInput={params => (
                  <TextFieldSearch
                    {...params}
                    placeholder={SELECT_GROUP}
                    variant='outlined'
                  />
                )}
                onChange={(e, newValue) => {
                  if (newValue?.id) {
                    setPage(0);
                    setOprations({
                      ...oprations,
                      page: 0,
                      groupId: newValue?.id?.toString(),
                    });
                  }
                }}
                renderOption={(props, option) => (
                  <Styled.SelectAutoCompleteGroupOptions>
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  </Styled.SelectAutoCompleteGroupOptions>
                )}
              />
              <Styled.SelectAutoCompleteEmail
                disablePortal
                id='select-audience-email'
                options={emails}
                disabled={isAudienceLoading}
                popupIcon={<CaretDown />}
                value={emails.find(a => a?.id === `${oprations.email}`)?.label}
                renderInput={params => (
                  <TextFieldSearch
                    {...params}
                    placeholder={AUDIENCE_SELECT_EMAIL_AUTO_COMPLETE}
                    variant='outlined'
                  />
                )}
                onChange={(e, newValue) => {
                  if (newValue?.id) {
                    setPage(0);
                    setOprations({
                      ...oprations,
                      page: 0,
                      email: newValue?.id,
                    });
                  }
                }}
                renderOption={(props, option) => (
                  <Styled.SelectAutoCompleteGroupOptions>
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  </Styled.SelectAutoCompleteGroupOptions>
                )}
              />
            </Styled.SearchArea>
          </Box>

          <ModeledAudienceDataTable
            data={modeledAudiences}
            onCheckboxChange={toggleAudience}
            selectedAudiences={selectedAudiences}
            oprations={oprations}
            page={page}
            setPage={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default CombinedAudienceBuilder;
